import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Edit2, RefreshCw, GitBranch, XCircle, Clock, Car, Home, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import PolicyModal from '../components/policies/PolicyModal';

const STATUS_LABEL = { active: 'Vigente', endorsed: 'Endosada', cancelled: 'Cancelada', expired: 'Vencida' };
const STATUS_CLASS = { active: 'badge-green', endorsed: 'badge-blue', cancelled: 'badge-red', expired: 'badge-amber' };

function EndorseModal({ policy, onClose }) {
  const { endorsePolicy } = useApp();
  const [type, setType] = useState('vehicle');
  const [desc, setDesc] = useState('');
  const [updates, setUpdates] = useState({});
  const setU = (k, v) => setUpdates(u => ({ ...u, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    endorsePolicy(policy.id, { type, description: desc, policyUpdates: type === 'coverage' ? { coverage: updates.coverage } : { assetData: { ...policy.assetData, ...updates } } });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Registrar endoso</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Tipo de endoso</label>
              <select value={type} onChange={e => setType(e.target.value)}>
                <option value="vehicle">Cambio de vehículo</option>
                <option value="coverage">Modificación de cobertura</option>
                <option value="other">Otro</option>
              </select>
            </div>
            {type === 'vehicle' && (
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Nueva marca</label><input value={updates.brand || ''} onChange={e => setU('brand', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Nuevo modelo</label><input value={updates.model || ''} onChange={e => setU('model', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Año</label><input value={updates.year || ''} onChange={e => setU('year', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Nueva matrícula</label><input value={updates.plate || ''} onChange={e => setU('plate', e.target.value)} /></div>
              </div>
            )}
            {type === 'coverage' && (
              <div className="form-group"><label className="form-label">Nueva cobertura</label><input value={updates.coverage || ''} onChange={e => setU('coverage', e.target.value)} placeholder="Ej: Todo riesgo" /></div>
            )}
            <div className="form-group">
              <label className="form-label">Descripción / Observaciones *</label>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} required rows={3} placeholder="Detallá el motivo o los cambios realizados..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary"><GitBranch size={13} /> Registrar endoso</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RenewModal({ policy, onClose }) {
  const { renewPolicy, companies } = useApp();
  const [submit, setSubmit] = useState(false);
  const [company, setCompany] = useState(policy.company || '');
  const [note, setNote] = useState('');
  const currentEnd = policy.endDate;
  const newEnd = format(new Date(new Date(currentEnd).setFullYear(new Date(currentEnd).getFullYear() + 1)), 'yyyy-MM-dd');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmit(true);
    try {
      await renewPolicy(policy.id, { company, note });
      onClose();
    } catch (err) {
      setSubmit(false);
      alert('Error al renovar: ' + err.message);
    }
  };

  // Lista de compañías para el selector (de la tabla + la actual por las dudas)
  const companyNames = Array.from(new Set([
    ...(companies || []).map(c => c.name),
    ...(policy.company ? [policy.company] : []),
  ])).sort((a, b) => a.localeCompare(b));

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Renovar póliza</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ background: 'var(--accent-dim)', border: '1px solid rgba(124,108,248,0.2)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Vigencia actual hasta:</span>
                <strong>{fmtPatron(currentEnd, 'dd/MM/yyyy')}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Nueva vigencia hasta:</span>
                <strong style={{ color: 'var(--accent)' }}>{fmtPatron(newEnd, 'dd/MM/yyyy')}</strong>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}>Compañía (podés cambiarla en esta renovación)</label>
              <select value={company} onChange={e => setCompany(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14 }}>
                {companyNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              {company && company !== policy.company && (
                <p style={{ fontSize: 12, color: 'var(--amber)', margin: '6px 0 0' }}>
                  Cambia de <strong>{policy.company || '—'}</strong> a <strong>{company}</strong> — quedará registrado en el historial.
                </p>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}>Comentario (opcional, queda en el historial)</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Ej. renovó con aumento de cobertura, cambió forma de pago, etc."
                style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, resize: 'vertical' }} />
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 14 }}>Se renovará la póliza por 1 año adicional. El estado pasará a <strong>Vigente</strong>.</p>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={submit}><RefreshCw size={13} /> Confirmar renovación</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CancelModal({ policy, onClose }) {
  const { cancelPolicy } = useApp();
  const [reason, setReason] = useState('');
  const submit = (e) => { e.preventDefault(); cancelPolicy(policy.id, reason); onClose(); };
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Cancelar póliza</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 10, padding: 14, fontSize: 13, color: 'var(--red)' }}>
              Esta acción cancelará la póliza <strong>{policy.number}</strong>. Quedará en el reporte de cancelaciones para seguimiento futuro.
            </div>
            <div className="form-group">
              <label className="form-label">Motivo de cancelación *</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} required rows={3} placeholder="Ej: No renueva por precio, cambió de compañía, vendió el vehículo..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Volver</button>
            <button type="submit" className="btn btn-danger"><XCircle size={13} /> Confirmar cancelación</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PolicyDetailPage() {
  const { id } = useParams();
  const { policies, clients, updatePolicy, deletePolicy, loading } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState(null);

  // Si se llega con ?renovar=1 (desde el dashboard), abrir el modal de renovación
  useEffect(() => {
    if (searchParams.get('renovar') === '1') setModal('renew');
  }, [searchParams]);

  const policy = policies.find(p => p.id === id);
  if (!policy) return <div className="page"><p>{loading || !policies.length ? 'Cargando…' : 'Póliza no encontrada.'}</p></div>;

  const client = clients.find(c => c.id === policy.clientId) || {};
  const asset = policy.assetData || {};
  const isCancelled = policy.status === 'cancelled';

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar la póliza ${policy.number}${client.name ? ` de ${client.name}` : ''}?\n\nEsta acción es irreversible. El cliente NO se elimina.`)) return;
    await deletePolicy(policy.id);
    navigate('/policies');
  };

  return (
    <div className="page fade-in">
      <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
        <ArrowLeft size={13} /> Volver
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <h1 className="page-title" style={{ fontSize: 22 }}>{policy.number}</h1>
            <span className={`badge ${STATUS_CLASS[policy.status] || 'badge-gray'}`}>{STATUS_LABEL[policy.status]}</span>
          </div>
          <p className="page-subtitle">{policy.company} · {client.name}</p>
        </div>
        {isCancelled ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Una póliza cancelada por error tiene que poder editarse y
                reactivarse — antes quedaba sin ninguna acción posible. */}
            <button className="btn btn-secondary btn-sm" onClick={() => setModal('edit')}><Edit2 size={13} /> Editar</button>
            <button className="btn btn-amber btn-sm" onClick={() => {
              if (window.confirm('¿Reactivar esta póliza? Volverá a estado vigente.')) {
                updatePolicy(policy.id, {
                  status: 'active', cancelDate: '', cancelReason: '',
                  history: [...(policy.history || []), { type: 'reactivacion', date: format(new Date(), 'yyyy-MM-dd'), desc: 'Póliza reactivada' }],
                });
              }
            }}><RefreshCw size={13} /> Reactivar</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={13} /> Eliminar</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setModal('edit')}><Edit2 size={13} /> Editar</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setModal('endorse')}><GitBranch size={13} /> Endoso</button>
            <button className="btn btn-amber btn-sm" onClick={() => setModal('renew')}><RefreshCw size={13} /> Renovar</button>
            <button className="btn btn-danger btn-sm" onClick={() => setModal('cancel')}><XCircle size={13} /> Cancelar</button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={13} /> Eliminar</button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Policy info */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Datos de la póliza</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['Compañía', policy.company], ['Tipo', policy.type], ['Cobertura', policy.coverage],
              ['Prima', policy.premium ? '$' + Number(policy.premium).toLocaleString('es-UY') : '—'],
              ['Inicio', policy.startDate && fmtPatron(policy.startDate, 'dd/MM/yyyy')],
              ['Vencimiento', policy.endDate && fmtPatron(policy.endDate, 'dd/MM/yyyy')],
              isCancelled && ['Cancelada', policy.cancelDate && fmtPatron(policy.cancelDate, 'dd/MM/yyyy')],
              isCancelled && ['Motivo', policy.cancelReason],
            ].filter(Boolean).map(([k, v]) => v ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                <span style={{ fontWeight: 500, textAlign: 'right' }}>{String(v)}</span>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Asset info */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>
            {policy.type === 'auto' ? '🚗 Vehículo' : policy.type === 'home' ? '🏠 Inmueble' : '📄 Bien asegurado'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {policy.type === 'auto' && [
              ['Marca / Modelo', [asset.brand, asset.model].filter(Boolean).join(' ')],
              ['Año', asset.year], ['Matrícula', asset.plate],
              ['Motor', asset.engine], ['Padrón', asset.padron],
              ['VIN / Chasis', asset.vin], ['Categoría', asset.category],
              ['Uso', asset.use], ['Zona', asset.zone],
              ['Combustible', asset.fuel], ['Color', asset.color],
            ].map(([k, v]) => v ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
            {policy.type === 'home' && [
              ['Dirección', asset.address], ['Tipo', asset.type], ['Superficie', asset.area], ['Valor asegurado', asset.value ? 'USD ' + asset.value : ''],
            ].map(([k, v]) => v ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Client */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Cliente</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>{client.name}</div>
            {client.dni && <div style={{ color: 'var(--text-secondary)' }}>CI/RUT: {client.dni}</div>}
            {client.phone && <a href={`tel:${client.phone}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{client.phone}</a>}
            {client.email && <a href={`mailto:${client.email}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{client.email}</a>}
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: 'fit-content' }} onClick={() => navigate(`/clients/${client.id}`)}>Ver ficha completa</button>
          </div>
        </div>

        {/* History */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>
            <Clock size={12} style={{ display: 'inline', marginRight: 6 }} />Historial
          </h3>
          {(!policy.history || policy.history.length === 0) ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin movimientos registrados.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[...policy.history].reverse().map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: h.type === 'endoso' ? 'var(--blue)' : h.type === 'renovacion' ? 'var(--accent)' : 'var(--red)', marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{h.type}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{h.date} · {h.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal === 'edit' && <PolicyModal policy={policy} onClose={() => setModal(null)} />}
      {modal === 'endorse' && <EndorseModal policy={policy} onClose={() => setModal(null)} />}
      {modal === 'renew' && <RenewModal policy={policy} onClose={() => setModal(null)} />}
      {modal === 'cancel' && <CancelModal policy={policy} onClose={() => setModal(null)} />}
    </div>
  );
}
